import React, { useState } from "react";
import { Avatar, Box, IconButton, Menu, MenuItem, Stack, Tooltip, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight, Settings, Error } from "@mui/icons-material";
import { Nav_Buttons, Profile_Menu } from "../../data";
import Logo from "../../assets/Images/logo.png";
import { useNavigate, useLocation, Link } from "react-router-dom";
import SettingsDrawer from "../../components/settings/drawer";
import { useDispatch, useSelector } from "react-redux";
import { changeSelection } from "../../components/Conversation/contactSlice";
import useSettings from "../../hooks/useSettings";
import { NAVBAR } from "../../config";
import stringToColor from "../../utils/stringToColor";
import { logoutUser, changePassword } from "../../apiservices";
import getContrastTextColor from "../../utils/getContrastTextColor";
import ChangePasswordModal from "../../pages/dashboard/ChangePasswordModal";
import { toast } from "react-toastify";
import { useSystemHealth } from "../../contexts/SystemHealthContext";

const getPath = (index) => {
  switch (index) {
    case 0:
      return "/app";
    case 1:
      return "/template";
    case 2:
      return "/services";
    case 3:
      return "/stylists";
    case 4:
      return "/appointments";
    case 5:
      return "/test-flow";
    default:
      break;
  }
};

const getIndex = (path) => {
  if (path.startsWith("/appointments")) return 4;
  if (path.startsWith("/test-flow")) return 5;
  if (path.startsWith("/app")) return 0;
  if (path.startsWith("/template")) return 1;
  if (path.startsWith("/services")) return 2;
  if (path.startsWith("/stylists")) return 3;
  return 0;
};


const SideBar = () => {
  const username = localStorage.getItem("username") || " ";
  const avatarColor = stringToColor(username);
  const textColor = getContrastTextColor(avatarColor);
  const { sidebarOpen, onToggleSidebar } = useSettings();
  const { isBookingDisabled, bookingDisabledReasons } = useSystemHealth();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const { selected } = useSelector((state) => state.contactList);
  const dispatch = useDispatch();
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsDrawerToggle = () => {
    setSettingsDrawerOpen((prev) => !prev);
  };

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [openChangePassword, setOpenChangePassword] = useState(false);

  const handleMenuClick = (index) => {
    handleClose(); // Close the menu
    if (index === 0) {
      // Change Password
      setOpenChangePassword(true);
    } else if (index === 1) {
      // Logout
      logoutUser().then(() => {
        localStorage.clear();
        navigate("/");
      }).catch(() => {
        localStorage.clear();
        navigate("/");
      });
    }
  };

  const handleChangePassword = async (oldPassword, newPassword) => {
    try {
      const response = await changePassword({ oldPassword, newPassword });
      if (response.data.success) {
        toast.success(response.data.message);
        setOpenChangePassword(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to change password");
    }
  };

  React.useEffect(() => {
    const index = getIndex(location.pathname);
    if (selected !== index) {
      dispatch(changeSelection(index));
    }
  }, [location.pathname, dispatch, selected]);

  const sidebarColors = {
    backgroundColor: 'hsl(214 32% 95%)',
    color: 'hsl(222 47% 11%)',
    activeBackgroundColor: 'hsl(214 32% 91%)',
  };

  return (
    <>
      <Box
        p={2}
        sx={{
          backgroundColor: sidebarColors.backgroundColor,
          color: sidebarColors.color,
          boxShadow: "0px 0px 2px rgba(0,0,0,0.25)",
          height: "100vh",
          width: sidebarOpen ? NAVBAR.DASHBOARD_WIDTH : NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          display: "flex",
          position: "sticky",
          top: 0,
          zIndex: theme.zIndex.drawer,
        }}
      >
        <IconButton
          onClick={onToggleSidebar}
          sx={{
            position: 'absolute',
            top: '50%',
            right: 0,
            transform: 'translate(50%, -50%)',
            backgroundColor: 'white',
            border: '1px solid hsl(214 32% 91%)',
            boxShadow: theme.shadows[3],
            zIndex: theme.zIndex.drawer + 1,
            '&:hover': {
              backgroundColor: 'hsl(214 32% 95%)',
            }
          }}
        >
          {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>

        <Stack
          direction="column"
          alignItems={"center"}
          justifyContent="space-between"
          sx={{ width: "100%", height: "100%" }}
          spacing={2}
        >
          <Stack alignItems={"center"} spacing={2} sx={{ width: "100%" }}>
            <Box
              sx={{
                height: 70,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              {sidebarOpen ? <img src={Logo} alt={"Logo icon"} style={{ width: "90%", maxHeight: "90%", objectFit: "contain" }} /> : <img src={Logo} alt={"Logo icon"} style={{ width: "100%", maxHeight: "100%", objectFit: "contain" }} />}
            </Box>
            <Stack
              sx={{ width: "100%" }}
              direction="column"
              alignItems="stretch"
              spacing={1}
            >
              {Nav_Buttons.map((el) => (
                <Tooltip title={el.title} placement="right" key={el.index} disableHoverListener={sidebarOpen}>
                  <ListItemButton
                    component={Link}
                    to={getPath(el.index)}
                    onClick={() => {
                      dispatch(changeSelection(el.index));
                    }}
                    sx={{
                      borderRadius: 1,
                      py: 1,
                      px: 1.5,
                      minHeight: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
                      backgroundColor: el.index === selected ? sidebarColors.activeBackgroundColor : "transparent",
                      '&:hover': {
                        backgroundColor: sidebarColors.activeBackgroundColor,
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, color: 'inherit', marginRight: 0 }}>
                      {el.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={el.title}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      sx={{
                        opacity: sidebarOpen ? 1 : 0,
                        width: sidebarOpen ? 'auto' : 0,
                        ml: sidebarOpen ? 2 : 0,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        transition: theme.transitions.create(['opacity', 'width', 'margin'], {
                          easing: theme.transitions.easing.sharp,
                          duration: theme.transitions.duration.enteringScreen,
                        }),
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              ))}
            </Stack>
          </Stack>

          <Stack alignItems={"center"} spacing={2} sx={{ width: "100%" }}>
            <Box
              sx={{
                height: 60,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
            </Box>
            <Stack
              sx={{ width: "100%" }}
              direction="column"
              alignItems="stretch"
              spacing={1}
            >
              {/* Alert Icon */}
              {isBookingDisabled && (
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Automatic appointment booking is disabled due to:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {bookingDisabledReasons.map((reason, i) => (
                          <li key={i}><Typography variant="caption">{reason}</Typography></li>
                        ))}
                      </ul>
                    </Box>
                  }
                  placement="right"
                  arrow
                  disableHoverListener={sidebarOpen}
                >
                  <ListItemButton
                    sx={{
                      borderRadius: 1,
                      py: 1,
                      px: 1.5,
                      minHeight: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 0, 0, 0.08)', // Light red hover
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, color: 'error.main', marginRight: 0 }}>
                      <Error color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={"System Alert"}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'error.main' }}
                      sx={{
                        opacity: sidebarOpen ? 1 : 0,
                        width: sidebarOpen ? 'auto' : 0,
                        ml: sidebarOpen ? 2 : 0,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        transition: theme.transitions.create(['opacity', 'width', 'margin'], {
                          easing: theme.transitions.easing.sharp,
                          duration: theme.transitions.duration.enteringScreen,
                        }),
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              )}

              <Tooltip title={"Settings"} placement="right" disableHoverListener={sidebarOpen}>
                <ListItemButton
                  onClick={handleSettingsDrawerToggle}
                  sx={{
                    borderRadius: 1,
                    py: 1,
                    px: 1.5,
                    minHeight: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
                    '&:hover': {
                      backgroundColor: sidebarColors.activeBackgroundColor,
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, color: 'inherit', marginRight: 0 }}>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText
                    primary={"Settings"}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    sx={{
                      opacity: sidebarOpen ? 1 : 0,
                      width: sidebarOpen ? 'auto' : 0,
                      ml: sidebarOpen ? 2 : 0,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: theme.transitions.create(['opacity', 'width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                      }),
                    }}
                  />
                </ListItemButton>
              </Tooltip>
              <Tooltip title={username} placement="right" disableHoverListener={sidebarOpen}>
                <ListItemButton
                  onClick={handleClick}
                  sx={{
                    borderRadius: 1,
                    py: 1,
                    px: 1.5,
                    minHeight: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
                    '&:hover': {
                      backgroundColor: sidebarColors.activeBackgroundColor,
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, color: 'inherit', marginRight: 0 }}>
                    <Avatar
                      id="basic-button"
                      sx={{ width: 24, height: 24, backgroundColor: avatarColor, color: textColor, fontSize: 14 }}
                      aria-controls={open ? "basic-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? "true" : undefined}
                    >
                      {username.charAt(0).toUpperCase()}
                    </Avatar>

                  </ListItemIcon>
                  <ListItemText
                    primary={username}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    sx={{
                      opacity: sidebarOpen ? 1 : 0,
                      width: sidebarOpen ? 'auto' : 0,
                      ml: sidebarOpen ? 2 : 0,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      transition: theme.transitions.create(['opacity', 'width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                      }),
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <SettingsDrawer open={settingsDrawerOpen} onClose={handleSettingsDrawerToggle} />
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Stack spacing={1} px={1}>
          {Profile_Menu.map((el, idx) => (
            <MenuItem onClick={() => {
              handleMenuClick(idx);
            }} key={idx}>
              <Stack
                sx={{ width: 150, gap: 1 }}
                direction="row"
                alignItems={"center"}
              >
                {el.icon}
                <span>{el.title}</span>
              </Stack>{" "}
            </MenuItem>
          ))}
        </Stack>
      </Menu>
      {openChangePassword && (
        <ChangePasswordModal
          open={openChangePassword}
          handleClose={() => setOpenChangePassword(false)}
          handleChangePassword={handleChangePassword}
        />
      )}
    </>
  );
};

export default SideBar;
